import { TemplateElement, html, defineElement } from '@webtides/element-js';
import { classMap } from 'lit-html/directives/class-map.js';

import '../../../legacy-library/webcomponents/lazy-src/lazy-src';
import LazySrcEvents from '../../../legacy-library/webcomponents/lazy-src/LazySrcEvents.js';
import ImageMap from './prod-image.map';

//prefiltered 3by2 map for faster preFilter Mode
const FilteredImageMap = ImageMap.filter(img => Math.abs(img[0] / img[1] - 3 / 2) < 0.1);
window.filteredMap = FilteredImageMap;

export const getDimensionsByBreakpoint = breakpoint => {
    const { width, variation, height } = breakpoint;

    if (height) {
        return ImageMap.find(entry => entry[0] === width && entry[1] === height);
    } else if (variation) {
        // calc computed height
        let closestDimensions = [];
        const ratioParts = variation.split('by');
        const computedHeight = ratioParts[0] / ratioParts[1];
        // prefilter based on width to find closest within 100px
        const widthMap = ImageMap.filter(entry => {
            const range = 100;
            if (entry[0] >= width && entry[0] - width < range) {
                return true;
            }
            return entry[1] >= height && entry[1] - height < range;
        });

        let delta = -1;
        widthMap.forEach(entry => {
            const [entryWidth, entryHeight] = entry;
            const wDelta = width - entryWidth;
            const hDelta = computedHeight - entryHeight;

            if (delta < 0) {
                delta = wDelta + hDelta;
                closestDimensions = entry;
            }

            if (hDelta > 0 && (delta < 0 || delta > wDelta + hDelta)) {
                //new "closest" entry
                closestDimensions = entry;
            }
        });
        return closestDimensions;
    }
};

export const getFilteredDimensionsByBreakpoint = breakpoint => {
    const { width } = breakpoint;
    return FilteredImageMap.find(entry => entry[0] >= Math.floor(width));
};

export const getSrcByBreakpoint = (src, breakpoint, ratio) => {
    const dimensions = ratio ? getFilteredDimensionsByBreakpoint(breakpoint) : getDimensionsByBreakpoint(breakpoint);
    return src.replace('{{width}}x{{height}}', dimensions.join('x'));
};

import styles from './_prod-image.css';
export default class ProdImage extends TemplateElement {
    constructor() {
        super({ shadowRender: false, styles: [styles] });
    }

    events() {
        return {
            img: {
                error: () => {
                    if (this.loading === false) {
                        //got to error state just for proper prod-images
                        this.error = true;
                    }
                },
            },
            'lazy-src': {
                [LazySrcEvents.IMG_ERROR]: () => {
                    this.error = true;
                },
                [LazySrcEvents.IMG_LOAD]: () => {
                    this.loading = false;
                },
            },
        };
    }

    properties() {
        return {
            loading: true,
            lazyPlaceholder: false,
            error: false,
            imgClass: '',
            pictureClass: '',
            breakpoints: [],
            src: '',
            alt: '',
            lazy: true,
            threeBy2Filter: true,
            positionX: 'center',
            positionY: 'center',
            cover: false,
            contain: false,
            scaleDown: true,
            reset: false,
        };
    }

    watch() {
        return {
            src: async () => {
                this.reset = true;
                await this.requestUpdate();
                this.reset = false;
                this.loading = true;
                this.error = false;
            },
        };
    }

    getSources() {
        const sorted = this.breakpoints.sort((first, second) => second.from - first.from);
        return sorted.map(breakpoint => {
            const scrset = getSrcByBreakpoint(this.src, breakpoint, this.threeBy2Filter);
            return html` <source media="(min-width: ${breakpoint.from}px)" srcset="${scrset}" /> `;
        });
    }

    loadInstant() {
        if (this.$refs.lazy) {
            //trigger instant load
            this.$refs.lazy?._observer.triggerLoad(this.$refs.lazyPicture);
        }
    }

    get imgCSSClasses() {
        const fitClasses = {
            'object-left': this.positionX === 'left',
            'object-right': this.positionX === 'right',
            'object-top': this.positionY === 'top',
            'object-bottom': this.positionY === 'bottom',
            'object-cover': this.cover,
            'object-contain': this.contain,
            'object-scale-down': this.scaleDown,
        };
        return Object.entries(fitClasses)
            .filter(([key, value]) => value === true)
            .map(([key]) => key)
            .join(' ');
    }
    get fallBackImage() {
        return window.siteConfig().fallBackImage;
    }

    renderFallbackImage() {
        return html`
            <picture class="fallback-image ${this.pictureClass}">
                <img
                    data-fallback="true"
                    class="${this.imgCSSClasses} ${this.imgClass}"
                    alt="prod-image-fallback"
                    src="${this.fallBackImage}"
                />
            </picture>
        `;
    }

    renderLoadingImage() {
        return html`<loading-spinner class="absolute inset-0 items-center justify-center"></loading-spinner>`;
    }

    template() {
        if (this.reset) {
            return html``;
        }
        if (this.error) {
            return this.renderFallbackImage();
        }
        if (this.lazy) {
            return html`
                <lazy-src ref="lazy" img-class="${this.imgCSSClasses} ${this.imgClass}" img-alt="${this.alt}">
                    <picture ref="lazyPicture" class="${this.pictureClass}"> ${this.getSources()} </picture>
                </lazy-src>
                ${this.lazyPlaceholder && this.loading ? this.renderLoadingImage() : ''}
            `;
        } else {
            return html`
                <picture class="${this.pictureClass}">
                    ${this.getSources()}
                    <img class="${this.imgCSSClasses} ${this.imgClass}" alt="${this.alt}" />
                </picture>
            `;
        }
    }
}

defineElement('prod-image', ProdImage);
